import { useState } from 'react';
import { Badge, Button, Field, Section, Textarea } from '@zt/ui';
import type { Tone } from '@zt/ui';
import { HelpTip } from '../lib/help';
import { useT } from '../lib/i18n';

export type ReviewStatus = 'not_required' | 'pending' | 'approved' | 'rejected';
export interface ClaimFinding {
  match: string;
  code: string;
  excerpt: string;
}

export const reviewTone: Record<ReviewStatus, Tone> = {
  not_required: 'neutral',
  pending: 'amber',
  approved: 'moss',
  rejected: 'rose',
};

/**
 * Uzman (veteriner) incelemesi: sağlık beyanı bulguları, durum, onay / gerekçeli ret. Sayfa ve bannerlar
 * ortak kullanır. Karar kaydedilmiş içeriğe verilir; kaydedilmemiş değişiklik varken düğmeler kapalıdır.
 */
export function ContentReviewPanel({
  status,
  note: reviewNote,
  findings,
  required,
  dirty,
  canReview,
  busy,
  onDecide,
}: {
  status: ReviewStatus;
  note: string | null;
  findings: ClaimFinding[];
  required: boolean;
  dirty: boolean;
  canReview: boolean;
  busy: boolean;
  onDecide: (decision: 'approved' | 'rejected', note: string) => void;
}) {
  const t = useT();
  const [note, setNote] = useState('');
  if (!required && !findings.length) return null;
  return (
    <Section
      title={t('admin.pages.review')}
      description={t('admin.pages.reviewLead')}
      action={<HelpTip k="pages.review" />}
    >
      <div className="space-y-3">
        <Badge tone={reviewTone[status]}>{t(`admin.pages.reviews.${status}`)}</Badge>
        {reviewNote && (
          <p className="text-sm text-ink-2">{t('admin.pages.reviewNote', { note: reviewNote })}</p>
        )}
        {findings.length > 0 && (
          <div className="rounded-md border border-amber/30 bg-amber-tint p-3 text-sm">
            <p className="font-medium">{t('admin.pages.findings', { count: findings.length })}</p>
            <ul className="mt-1 list-disc space-y-0.5 ps-5">
              {findings.map((f, i) => (
                <li key={i}>
                  <strong>{f.match}</strong> — {t(`healthClaims.${f.code}`)}: “{f.excerpt}”
                </li>
              ))}
            </ul>
          </div>
        )}
        {canReview && status !== 'approved' && (
          <div className="grid max-w-xl gap-2">
            {dirty && <p className="text-sm text-ink-2">{t('admin.pages.reviewSaveFirst')}</p>}
            <Field
              label={t('admin.pages.reviewNoteLabel')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="pages.review_note" />}
            >
              {(p) => (
                <Textarea {...p} value={note} maxLength={1000} onChange={(e) => setNote(e.target.value)} />
              )}
            </Field>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                disabled={busy || dirty}
                onClick={() => onDecide('approved', note.trim())}
              >
                {t('admin.pages.approve')}
              </Button>
              <Button
                variant="danger"
                disabled={busy || dirty || !note.trim()}
                onClick={() => onDecide('rejected', note.trim())}
              >
                {t('admin.pages.reject')}
              </Button>
              <HelpTip k="pages.review_decision" />
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}
